import React from 'react';
import Markdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkSupersub from 'remark-supersub';

import { Heading, Paragraph, Text } from '@eluve/components';

interface ChatMarkdownProps {
  children: string;
}

const markdownComponents: Components = {
  ul: ({ children }) => (
    <ul className="my-2 ml-4 list-outside list-disc space-y-1">{children}</ul>
  ),
  ol: ({ children, start }) => (
    <ol start={start} className="my-2 ml-4 list-outside list-decimal space-y-1">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="my-0.5 pl-1">
      <Text size="md" color="primary" className="leading-relaxed">
        {children}
      </Text>
    </li>
  ),
  h1: ({ children }) => (
    <Heading size="md" weight="bold" color="primary" className="mb-2 mt-4">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Text size="lg" weight="bold" color="primary" className="mb-2 mt-3">
      {children}
    </Text>
  ),
  h3: ({ children }) => (
    <Text size="md" weight="bold" color="primary" className="mb-2 mt-3">
      {children}
    </Text>
  ),
  h4: ({ children }) => (
    <Text size="sm" weight="bold" color="primary" className="mb-2 mt-3">
      {children}
    </Text>
  ),
  strong: ({ children }) => (
    <Text weight="bold" color="primary" size="md">
      {children}
    </Text>
  ),
  em: ({ children }) => (
    <Text weight="medium" color="secondary" size="md">
      {children}
    </Text>
  ),
  p: ({ children }) => (
    <Paragraph size="md" className="my-3 whitespace-pre-line leading-relaxed">
      {children}
    </Paragraph>
  ),
  sup: ({ children, ...props }) => {
    // Extract citation number (e.g., "[1]" -> "1")
    const childrenStr = String(children);
    const citationMatch = childrenStr.match(/^\[(\d+)\]$/);
    const citationNumber = citationMatch ? citationMatch[1] : null;

    return (
      <sup
        {...props}
        className="cursor-pointer text-text-lightblue hover:text-text-lightblue-strong"
        data-citation={citationNumber}
      >
        {children}
      </sup>
    );
  },
};

export const ChatMarkdown: React.FC<ChatMarkdownProps> = ({ children }) => {
  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkSupersub as any]}
      components={markdownComponents}
    >
      {children}
    </Markdown>
  );
};
